import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  FileText, Download, Calendar, TrendingUp, Package, Users,
  ChevronDown, Loader2, BarChart3, FileSpreadsheet
} from 'lucide-react';

const ReportsPanel = () => {
  const { api } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('weekly');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const periods = [
    { key: 'weekly', label: 'Semanal', days: 7 },
    { key: 'monthly', label: 'Mensal', days: 30 },
    { key: 'quarterly', label: 'Trimestral', days: 90 },
    { key: 'semiannual', label: 'Semestral', days: 180 },
    { key: 'annual', label: 'Anual', days: 365 }
  ];

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().get(`/reports/${selectedPeriod}`);
      setReportData(res.data);
    } catch (err) {
      toast.error('Erro ao carregar relatório');
    }
    setLoading(false);
  }, [api, selectedPeriod]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const topProducts = useMemo(() => reportData?.top_products?.slice(0, 5) || [], [reportData]);
  const stockAlerts = useMemo(
    () => (reportData?.stock_summary || []).filter(s => s.quantity <= s.min_threshold).slice(0, 5),
    [reportData]
  );

  const downloadReport = async (format) => {
    setDownloading(format);
    try {
      const response = await api().get(`/reports/${selectedPeriod}/download/${format}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], {
        type: format === 'pdf' 
          ? 'application/pdf' 
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_${selectedPeriod}_${new Date().toISOString().slice(0,10)}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Relatório ${format.toUpperCase()} descarregado!`);
    } catch (err) {
      toast.error('Erro ao descarregar relatório');
    }
    setDownloading(null);
  };

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex flex-wrap gap-2">
        {periods.map((period) => (
          <button
            key={period.key}
            onClick={() => setSelectedPeriod(period.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              selectedPeriod === period.key
                ? 'bg-sonangol-orange text-white shadow-lg shadow-orange-500/30'
                : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
            }`}
            data-testid={`period-${period.key}`}
          >
            {period.label}
          </button>
        ))}
      </div>

      {/* Download Buttons */}
      <div className="flex gap-3">
        <Button
          onClick={() => downloadReport('pdf')}
          disabled={downloading !== null}
          className="bg-red-600 hover:bg-red-700 text-white"
          data-testid="download-pdf-btn"
        >
          {downloading === 'pdf' ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <FileText className="w-4 h-4 mr-2" />
          )}
          Descarregar PDF
        </Button>
        <Button
          onClick={() => downloadReport('excel')}
          disabled={downloading !== null}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="download-excel-btn"
        >
          {downloading === 'excel' ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <FileSpreadsheet className="w-4 h-4 mr-2" />
          )}
          Descarregar Excel
        </Button>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-sonangol-orange" />
        </div>
      ) : reportData ? (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-zinc-700/50 rounded-xl p-4 border border-zinc-600">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-xs">Total Pedidos</p>
                  <p className="text-white font-bold text-xl">{reportData.total_orders}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-700/50 rounded-xl p-4 border border-zinc-600">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-xs">Receita Total</p>
                  <p className="text-white font-bold text-xl">{reportData.total_revenue?.toLocaleString('pt-AO')} <span className="text-sm font-normal">AOA</span></p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-700/50 rounded-xl p-4 border border-zinc-600">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-xs">Ticket Médio</p>
                  <p className="text-white font-bold text-xl">{reportData.average_ticket?.toLocaleString('pt-AO')} <span className="text-sm font-normal">AOA</span></p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-700/50 rounded-xl p-4 border border-zinc-600">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-xs">Novos Clientes</p>
                  <p className="text-white font-bold text-xl">{reportData.new_customers}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Breakdown Chart */}
          {reportData.daily_breakdown?.length > 0 && (
            <div className="bg-zinc-700/50 rounded-xl p-5 border border-zinc-600">
              <h3 className="font-barlow font-bold text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-sonangol-orange" />
                Vendas por Período
              </h3>
              <div className="overflow-x-auto">
                <div className="flex gap-2 min-w-max pb-2">
                  {reportData.daily_breakdown.slice(-14).map((day, idx) => {
                    const maxRevenue = Math.max(...reportData.daily_breakdown.map(d => d.revenue));
                    const height = maxRevenue > 0 ? (day.revenue / maxRevenue) * 100 : 0;
                    
                    return (
                      <div key={day.date} className="flex flex-col items-center gap-1" style={{minWidth: '50px'}}>
                        <div className="h-24 w-8 bg-zinc-600 rounded-t relative flex items-end">
                          <div 
                            className="w-full bg-gradient-to-t from-sonangol-orange to-orange-400 rounded-t transition-all"
                            style={{ height: `${Math.max(height, 2)}%` }}
                          />
                        </div>
                        <span className="text-xs text-zinc-400">{day.date}</span>
                        <span className="text-xs text-zinc-300 font-medium">{day.orders}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-between text-xs text-zinc-500 mt-2">
                <span>Pedidos por dia</span>
                <span>Total: {reportData.daily_breakdown.reduce((sum, d) => sum + d.orders, 0)} pedidos</span>
              </div>
            </div>
          )}

          {/* Top Products */}
          {reportData.top_products?.length > 0 && (
            <div className="bg-zinc-700/50 rounded-xl p-5 border border-zinc-600">
              <h3 className="font-barlow font-bold text-white mb-4">Produtos Mais Vendidos</h3>
              <div className="space-y-3">
                {topProducts.map((product, idx) => (
                  <div key={product.product_id || product.name} className="flex items-center justify-between py-2 border-b border-zinc-600 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-sonangol-orange/20 rounded-full flex items-center justify-center text-sonangol-orange text-xs font-bold">
                        {idx + 1}
                      </span>
                      <span className="text-zinc-200">{product.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-medium">{product.quantity_sold} un.</p>
                      <p className="text-xs text-zinc-400">{product.revenue?.toLocaleString('pt-AO')} AOA</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stock Summary */}
          {reportData.stock_summary?.length > 0 && (
            <div className="bg-zinc-700/50 rounded-xl p-5 border border-zinc-600">
              <h3 className="font-barlow font-bold text-white mb-4">Estado do Stock (Alertas)</h3>
              <div className="space-y-2">
                {stockAlerts.map((item) => (
                  <div key={item.product_id || item.product_name} className="flex items-center justify-between py-2 px-3 bg-zinc-600/30 rounded-lg">
                    <span className="text-zinc-200">{item.product_name}</span>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        item.quantity === 0 
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {item.quantity === 0 ? 'Esgotado' : 'Baixo'}
                      </span>
                      <span className="text-zinc-300 font-medium">{item.quantity} un.</span>
                    </div>
                  </div>
                ))}
                {reportData.stock_summary.filter(s => s.quantity <= s.min_threshold).length === 0 && (
                  <p className="text-zinc-400 text-sm text-center py-4">Todos os produtos com stock normal</p>
                )}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ReportsPanel;
